<script setup lang="ts">
import { BaseSerializedChart } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import { computed, ComputedRef, ref, Ref, watch } from 'vue'
import { CHART_TYPES, ChartType } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'
import _ from 'lodash'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

const model = defineModel<BaseSerializedChart | null>()

const { t } = useI18n()
const { mobile } = useDisplay()

const title: Ref<string | undefined> = ref(t('chart_sharing.creator.customize.placeholder.title'))
const subtitle: Ref<string | undefined> = ref(t('chart_sharing.creator.customize.placeholder.subtitle'))
const chartType: Ref<ChartType> = ref<ChartType>(CHART_TYPES.LINE)
const yAxisType: Ref<'linear' | 'logarithmic' | undefined> = ref(undefined)

const compiledModel: ComputedRef<BaseSerializedChart> = computed(() => {
  return {
    chartType: chartType.value,
    ...(title.value && { title: title.value }),
    ...(subtitle.value && { subtitle: subtitle.value }),
    ...(yAxisType.value && { yAxisType: yAxisType.value }),
  }
})

const availableChartTypes: ChartType[] = Object.values(CHART_TYPES).filter(
  t => t !== CHART_TYPES.INCIDENT_BAR && t !== CHART_TYPES.DATABASE_ENTRIES_BAR
) as ChartType[]

watch(
  compiledModel,
  () => {
    if (_.isEqual(model.value, compiledModel.value)) return
    model.value = compiledModel.value
  },
  { immediate: true, deep: true }
)

watch(
  model,
  () => {
    if (_.isEqual(model.value, compiledModel.value)) return
    if (!model.value) return
    chartType.value = model.value.chartType
    title.value = model.value.title
    subtitle.value = model.value.subtitle
    yAxisType.value = model.value.yAxisType
  },
  { immediate: true, deep: true }
)
</script>

<template>
  <v-layout-card
    class="pa-0"
    variant="flat"
    elevation="0"
  >
    <v-card-title class="pt-0">
      <v-icon
        icon="mdi-information"
        start
      />
      {{ $t('chart_sharing.creator.customize.basic_information') }}
    </v-card-title>
    <v-card-text
      class="py-2"
      :class="{ 'px-0': mobile, 'px-6': !mobile }"
    >
      <v-text-field
        v-model="title"
        prepend-icon="mdi-format-header-1"
        variant="outlined"
        :label="$t('chart_sharing.creator.customize.chart_title')"
      />
      <v-text-field
        v-model="subtitle"
        prepend-icon="mdi-format-header-2"
        variant="outlined"
        :label="$t('chart_sharing.creator.customize.chart_subtitle')"
      />
      <v-autocomplete
        :model-value="chartType"
        prepend-icon="mdi-chart-line-variant"
        :items="availableChartTypes"
        :item-title="(item: ChartType) => $t(`chart_sharing.creator.customize.chart_types.${item}`)"
        :item-value="(item: ChartType) => item"
        variant="outlined"
        :label="$t('chart_sharing.creator.customize.chart_details.chart_type')"
        hide-details
        @update:model-value="val => (chartType = val)"
      />
    </v-card-text>
  </v-layout-card>
</template>
